import React, { useEffect, useState, useMemo } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { TabView, TabPanel } from 'primereact/tabview';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';

import SharedInputText from '../Shared/SharedInputText';
import SharedButton from '../Shared/SharedButton';
import DropdownComponent from '../Shared/DropdownComponent';
import AccountAutoComplete from '../Shared/AccountAutoComplete';
import { fetchData } from '../../helpers/fetchData';
import validSupergitHIS from '../../api/validSupergitHIS';
import { getCostCenter } from '../../store/slices/CostCenterSlice';
import PageHeader from '../Shared/PageHeader';

const GL_SETTINGS_CONFIG = [
    {
        tab: 'Cash Sale',
        section: 'General',
        key: 'cash_sale_cash_amount_with_vat',
        description: 'Cash Amount With VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Sale',
        section: 'General',
        key: 'cash_sale_bank_with_vat',
        description: 'Card Payments With VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Sale',
        section: 'General',
        key: 'cash_sale_bank_payment_cost',
        description: 'Bank Payment Cost (Fees)',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Sale',
        section: 'General',
        key: 'cash_sale_vat_amount',
        description: 'VAT Amount',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'General',
        key: 'cash_sale_{dept_slug}_revenue_without_vat',
        description: 'Department Revenue Without VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'General',
        key: 'total_patient_pay_credit_amount',
        description: 'Total Credit Paid By Patients',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Sale',
        section: 'General',
        key: '{category}_medicine_cost',
        description: 'Medicine Cost',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'Advance Deposits',
        key: 'advance_payment_{dept_slug}_revenue_without_vat',
        description: 'Advance Payment Dept Revenue',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'Advance Deposit Use',
        key: 'deposit_use_revenue',
        description: 'Deposit Use Revenue',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Sale',
        section: 'Advance Deposit Use',
        key: 'deposit_use_{dept_slug}_revenue_without_vat',
        description: 'Deposit Use Dept Revenue',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'Withdraw Advance',
        key: 'advance_payment_withdraw_without_vat',
        description: 'Advance Payment Withdraw Without VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'Withdraw Advance',
        key: 'advance_payment_withdraw_vat',
        description: 'Advance Payment Withdraw VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'Withdraw Advance',
        key: 'advance_payment_withdraw_with_vat',
        description: 'Advance Payment Withdraw With VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Sale',
        section: 'Patient Credit',
        key: 'total_patient_credit_amount',
        description: 'Total Patient Credit Amount',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Sale',
        section: 'Patient Credit',
        key: 'total_patient_pay_credit_amount',
        description: 'Total Credit Paid By Patients',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Credit',
        section: 'General',
        key: 'cash_credit_cash_credit_revenue_with_vat',
        description: 'Cash Credit Revenue With VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Cash Credit',
        section: 'General',
        key: 'cash_credit_cash_credit_vat',
        description: 'Cash Credit VAT Amount',
        gl_type: 'Credit'
    },
    {
        tab: 'Cash Credit',
        section: 'General',
        key: 'cash_credit_{dept_slug}_revenue_without_vat',
        description: 'Cash Credit Dept Revenue',
        gl_type: 'Credit'
    },
    {
        tab: 'Insurance',
        section: 'General',
        key: '{payer_id}',
        description: 'Payer Net Amount + VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Insurance',
        section: 'General',
        key: 'insurance_{dept_slug}_revenue_without_vat',
        description: 'Insurance Dept Revenue',
        gl_type: 'Credit'
    },
    {
        tab: 'Insurance',
        section: 'General',
        key: 'insurance_total_vat',
        description: 'Insurance Total VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Corporate',
        section: 'General',
        key: '{payer_id}',
        description: 'Payer Net Amount + VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Corporate',
        section: 'General',
        key: 'coperate_{dept_slug}_revenue_without_vat',
        description: 'Corporate Dept Revenue',
        gl_type: 'Credit'
    },
    {
        tab: 'Corporate',
        section: 'General',
        key: 'coperate_total_vat',
        description: 'Corporate Total VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Government',
        section: 'General',
        key: '{payer_id}',
        description: 'Payer Net Amount + VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Government',
        section: 'General',
        key: 'government_{dept_slug}_revenue_without_vat',
        description: 'Government Dept Revenue',
        gl_type: 'Credit'
    },
    {
        tab: 'Government',
        section: 'General',
        key: 'government_total_vat',
        description: 'Government Total VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Pharmacy Cash',
        section: 'General',
        key: 'cash_sales_pharma_payments_with_vat',
        description: 'Pharma Cash Payments With VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Pharmacy Cash',
        section: 'General',
        key: 'cash_sales_pharma_revenue_without_vat',
        description: 'Pharma Revenue Without VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Pharmacy Cash',
        section: 'General',
        key: 'cash_sales_pharma_vat_amount',
        description: 'Pharma VAT Amount',
        gl_type: 'Credit'
    },
    {
        tab: 'Pharmacy Insurance',
        section: 'General',
        key: '{payer_id}',
        description: 'Payer Net Amount + VAT',
        gl_type: 'Debit'
    },
    {
        tab: 'Pharmacy Insurance',
        section: 'General',
        key: 'insurance_pharma_revenue_without_vat',
        description: 'Pharma Insurance Revenue Without VAT',
        gl_type: 'Credit'
    },
    {
        tab: 'Pharmacy Insurance',
        section: 'General',
        key: 'insurance_pharma_vat_amount',
        description: 'Pharma Insurance VAT Amount',
        gl_type: 'Credit'
    }
];

const MEDICINE_CATEGORIES = [
    'medical-devices',
    'medication-codes',
    'transportation-srca',
    'imaging',
    'services',
    'laboratory',
    'oral-health-op',
    'procedures'
];

const CreateGLPostSetting = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const lang = i18next.language;
    const editData = location?.state?.editData;
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const [payerList, setPayerList] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const dispatch = useDispatch();
    const { costCenters } = useSelector(state => state.costCenter);

    useEffect(() => {
        const getPayerListData = async () => {
            try {
                const response = await validSupergitHIS.post('/his/getPayerList');
                if (response?.data) {
                    const data = response.data?.data;
                    setPayerList(data || []);
                }
            } catch (error) {
                console.log(error.message || t('failed_to_get_payer_list'));
            }
        };
        getPayerListData();
    }, [lang, t]);

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await fetchData('POST', 'his/departments/getList');
                if (response?.data) {
                    const options = response.data.map(dept => ({
                        label: dept.name_en,
                        value: dept.name_en
                            ?.toLowerCase()
                            .replace(/\s+/g, '_')
                            .replace(/_revenue_without_vat$/, '')
                    }));
                    setDepartments(options);
                }
            } catch (error) {
                console.error('Error fetching departments:', error);
            }
        };
        fetchDepartments();
        dispatch(getCostCenter());
    }, [dispatch]);

    const expandedConfig = useMemo(() => {
        let rows = [];

        GL_SETTINGS_CONFIG.forEach(cfg => {
            if (cfg.key.includes('{dept_slug}')) {
                if (departments.length > 0) {
                    departments.forEach(dept => {
                        rows.push({
                            ...cfg,
                            key: cfg.key.replace('{dept_slug}', dept.value),
                            description: `${cfg.description} (${dept.label})`,
                            isDynamic: true
                        });
                    });
                } else {
                    rows.push(cfg);
                }
            } else if (cfg.key.includes('{category}_medicine_cost')) {
                MEDICINE_CATEGORIES.forEach(cat => {
                    const label = cat
                        .split('-')
                        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ');
                    rows.push({
                        ...cfg,
                        key: cfg.key.replace('{category}', cat),
                        description: `${label} Medicine Cost`,
                        isDynamic: true
                    });
                });
            } else if (cfg.key === '{payer_id}') {
                let category = '';
                if (cfg.tab === 'Insurance' || cfg.tab === 'Pharmacy Insurance') category = 'insurance';
                else if (cfg.tab === 'Corporate') category = 'corporate';
                else if (cfg.tab === 'Government') category = 'government';

                const payers = payerList.filter(p => p.category === category);
                if (payers.length > 0) {
                    payers.forEach(payer => {
                        rows.push({
                            ...cfg,
                            key: cfg.tab === 'Pharmacy Insurance' ? `pharma_${payer.payer_id}` : payer.payer_id,
                            description: `${payer.name} - ${cfg.description}`,
                            isDynamic: true
                        });
                    });
                }
            } else {
                rows.push(cfg);
            }
        });

        return rows;
    }, [departments, payerList]);

    const initialValues = useMemo(() => {
        const existingMap = new Map();
        if (editData?.transactions) {
            editData.transactions.forEach(t => existingMap.set(t.amount_type, t));
        }

        const transactions = expandedConfig.map(cfg => {
            const existing = existingMap.get(cfg.key);
            return {
                account_type: existing?.account_type || 'general',
                account_code: existing?.account_code || '',
                account_name_en: existing?.account_name_en || '',
                account_name_ar: existing?.account_name_ar || '',
                transaction_type: existing?.transaction_type || cfg.gl_type.toLowerCase(),
                amount_type: cfg.key,
                cost_center_id: existing?.cost_center_id || null,
                _tab: cfg.tab,
                _section: cfg.section,
                _description: cfg.description,
                _gl_type_display: cfg.gl_type
            };
        });

        return {
            title: editData?.title || 'his_transactions_settings',
            transactions
        };
    }, [expandedConfig, editData]);

    const validationSchema = yup.object().shape({
        transactions: yup.array().of(yup.object().shape({}))
    });

    const handleCreateSettings = async payload => {
        try {
            setLoading(true);
            const res = await fetchData('POST', '/tsetting/create', payload);
            if (res) {
                toast.success(res?.message);
                navigate(-1);
            }
        } catch (error) {
            toast.error(error.message || t('failed_to_create_settings'));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async payload => {
        try {
            setLoading(true);
            const res = await fetchData('PUT', '/tsetting/update', payload);
            if (res) {
                toast.success(res?.message);
                navigate(-1);
            }
        } catch (error) {
            toast.error(error.message || t('failed_to_update_settings'));
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = values => {
        let payload = {
            ...(editData && { ...editData }),
            title: values?.title,
            transactions: values?.transactions
                .filter(t => t.account_code)
                .map(transaction => {
                    const { _tab, _section, _description, _gl_type_display, ...transactionData } = transaction;
                    return {
                        ...transactionData,
                        category: transactionData.amount_type,
                        account_code: String(transactionData?.account_code),
                        cost_center_id: transactionData?.cost_center_id ? Number(transactionData.cost_center_id) : null
                    };
                })
        };

        if (payload.transactions.length === 0) {
            toast.error('Please configure at least one transaction');
            return;
        }

        if (editData) {
            handleUpdateSettings(payload);
        } else {
            handleCreateSettings(payload);
        }
    };

    const tabs = useMemo(() => {
        return [...new Set(GL_SETTINGS_CONFIG.map(c => c.tab))];
    }, []);

    const getTabTransactions = (allValues, tabName) => {
        return allValues.map((t, index) => ({ ...t, index })).filter(t => t._tab === tabName);
    };

    return (
        <div>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
                enableReinitialize={true}
            >
                {props => (
                    <Form onSubmit={props.handleSubmit}>
                        <div className='mt-2'>
                            <PageHeader title={editData ? t('Update GL Postings Settings') : t('Create GL Postings Settings')} backPath={-1} />

                            <FieldArray name='transactions'>
                                {({ form }) => (
                                    <TabView
                                        activeIndex={activeIndex}
                                        onTabChange={e => setActiveIndex(e.index)}
                                        className='gl-settings-tabs'
                                    >
                                        {tabs.map(tabName => {
                                            const tabTransactions = getTabTransactions(form.values.transactions, tabName);

                                            // Group by section for Cash Sale tab
                                            const groupBySection = transactions => {
                                                const grouped = {};
                                                const sectionOrder = [];
                                                transactions.forEach(t => {
                                                    if (!grouped[t._section]) {
                                                        grouped[t._section] = [];
                                                        sectionOrder.push(t._section);
                                                    }
                                                    grouped[t._section].push(t);
                                                });
                                                return { grouped, sectionOrder };
                                            };

                                            const { grouped, sectionOrder } = groupBySection(tabTransactions);

                                            return (
                                                <TabPanel header={t(tabName)} key={tabName}>
                                                    <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
                                                        <div className='overflow-x-auto'>
                                                            <table className='min-w-full' dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                                                                <thead>
                                                                    <tr className='bg-gray-100 border-b border-gray-200'>
                                                                        <th
                                                                            className={`px-4 py-3 ${lang === 'ar' ? 'text-right' : 'text-left'} font-semibold text-gray-900 uppercase text-sm`}
                                                                            style={{ width: '400px' }}
                                                                        >
                                                                            {t('Description')}
                                                                        </th>
                                                                        <th
                                                                            className={`px-4 py-3 ${lang === 'ar' ? 'text-right' : 'text-left'} font-semibold text-gray-900 uppercase text-sm`}
                                                                            style={{ width: '300px' }}
                                                                        >
                                                                            {t('Account Code')}
                                                                        </th>
                                                                        <th
                                                                            className={`px-4 py-3 ${lang === 'ar' ? 'text-right' : 'text-left'} font-semibold text-gray-900 uppercase text-sm`}
                                                                        >
                                                                            {lang === 'ar' ? t('Account Name (AR)') : t('Account Name (EN)')}
                                                                        </th>
                                                                        <th
                                                                            className={`px-4 py-3 ${lang === 'ar' ? 'text-right' : 'text-left'} font-semibold text-gray-900 uppercase text-sm`}
                                                                            style={{ width: '200px' }}
                                                                        >
                                                                            {t('Cost Center')}
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className='divide-y divide-gray-100'>
                                                                    {tabName === 'Cash Sale'
                                                                        ? sectionOrder.map(sectionName => (
                                                                            <React.Fragment key={sectionName}>
                                                                                {sectionName?.toLowerCase() !== 'general' && (
                                                                                    <tr className='bg-gray-100'>
                                                                                        <td colSpan='4' className='px-4 py-3'>
                                                                                            <span className='font-semibold text-gray-700 text-sm'>
                                                                                                {t(sectionName)}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}

                                                                                {grouped[sectionName].map(transaction => {
                                                                                    const index = transaction.index;
                                                                                    return (
                                                                                        <tr key={index} className='hover:bg-gray-50 transition-colors'>
                                                                                            <td className='px-4 py-2 align-middle'>
                                                                                                <div className='font-medium text-gray-800 text-sm'>
                                                                                                    {transaction._description}
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className='px-4 py-2 align-middle'>
                                                                                                <AccountAutoComplete
                                                                                                    value={props.values.transactions[index]?.account_code}
                                                                                                    lang={lang}
                                                                                                    t={t}
                                                                                                    className='!h-[32px]'
                                                                                                    onAccountSelect={accountData => {
                                                                                                        props.setFieldValue(
                                                                                                            `transactions[${index}].account_code`,
                                                                                                            accountData.code
                                                                                                        );
                                                                                                        props.setFieldValue(
                                                                                                            `transactions[${index}].account_name_en`,
                                                                                                            accountData.name_en
                                                                                                        );
                                                                                                        props.setFieldValue(
                                                                                                            `transactions[${index}].account_name_ar`,
                                                                                                            accountData.name_ar
                                                                                                        );
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            <td className='px-4 py-2 align-middle'>
                                                                                                <SharedInputText
                                                                                                    name={`transactions[${index}].account_name_${lang === 'ar' ? 'ar' : 'en'}`}
                                                                                                    disabled
                                                                                                    className='!h-[32px] w-full bg-gray-50 border-gray-200'
                                                                                                />
                                                                                            </td>
                                                                                            <td className='px-4 py-2 align-middle'>
                                                                                                <DropdownComponent
                                                                                                    options={
                                                                                                        costCenters?.data
                                                                                                            ? Object.entries(costCenters.data).map(([id, center]) => ({
                                                                                                                label: lang === 'ar' ? center.name_ar : center.name_en,
                                                                                                                value: Number(center.id)
                                                                                                            }))
                                                                                                            : []
                                                                                                    }
                                                                                                    name={`transactions[${index}].cost_center_id`}
                                                                                                    value={props?.values?.transactions[index]?.cost_center_id}
                                                                                                    onChange={e => {
                                                                                                        props?.setFieldValue(
                                                                                                            `transactions[${index}].cost_center_id`,
                                                                                                            e.value
                                                                                                        );
                                                                                                    }}
                                                                                                    placeholder={t('Select Cost Center')}
                                                                                                    className='!w-full !h-[32px]'
                                                                                                />
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </React.Fragment>
                                                                        ))
                                                                        : tabTransactions.map(transaction => {
                                                                            const index = transaction.index;
                                                                            return (
                                                                                <tr key={index} className='hover:bg-gray-50 transition-colors'>
                                                                                    <td className='px-4 py-2 align-middle'>
                                                                                        <div className='font-medium text-gray-800 text-sm'>
                                                                                            {transaction._description}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className='px-4 py-2 align-middle'>
                                                                                        <AccountAutoComplete
                                                                                            value={props.values.transactions[index]?.account_code}
                                                                                            lang={lang}
                                                                                            t={t}
                                                                                            className='!h-[32px]'
                                                                                            onAccountSelect={accountData => {
                                                                                                props.setFieldValue(
                                                                                                    `transactions[${index}].account_code`,
                                                                                                    accountData.code
                                                                                                );
                                                                                                props.setFieldValue(
                                                                                                    `transactions[${index}].account_name_en`,
                                                                                                    accountData.name_en
                                                                                                );
                                                                                                props.setFieldValue(
                                                                                                    `transactions[${index}].account_name_ar`,
                                                                                                    accountData.name_ar
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                    <td className='px-4 py-2 align-middle'>
                                                                                        <SharedInputText
                                                                                            name={`transactions[${index}].account_name_${lang === 'ar' ? 'ar' : 'en'}`}
                                                                                            disabled
                                                                                            className='!h-[32px] w-full bg-gray-50 border-gray-200'
                                                                                        />
                                                                                    </td>
                                                                                    <td className='px-4 py-2 align-middle'>
                                                                                        <DropdownComponent
                                                                                            options={
                                                                                                costCenters?.data
                                                                                                    ? Object.entries(costCenters.data).map(([id, center]) => ({
                                                                                                        label: lang === 'ar' ? center.name_ar : center.name_en,
                                                                                                        value: Number(center.id)
                                                                                                    }))
                                                                                                    : []
                                                                                            }
                                                                                            name={`transactions[${index}].cost_center_id`}
                                                                                            value={props?.values?.transactions[index]?.cost_center_id}
                                                                                            onChange={e => {
                                                                                                props?.setFieldValue(
                                                                                                    `transactions[${index}].cost_center_id`,
                                                                                                    e.value
                                                                                                );
                                                                                            }}
                                                                                            placeholder={t('Select Cost Center')}
                                                                                            className='!w-full !h-[32px]'
                                                                                        />
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    {tabTransactions.length === 0 && (
                                                                        <tr>
                                                                            <td colSpan='5' className='text-center py-8 text-gray-500'>
                                                                                {t('No settings available for this tab')}
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </TabPanel>
                                            );
                                        })}
                                    </TabView>
                                )}
                            </FieldArray>

                            <div className='flex justify-end gap-4 mt-6'>
                                <SharedButton
                                    icon='pi pi-times'
                                    label={t('Cancel')}
                                    type='button'
                                    buttonType='reset'
                                    onClick={() => navigate(-1)}
                                    disabled={loading}
                                />
                                <SharedButton
                                    type='submit'
                                    loading={loading}
                                    icon='pi pi-check'
                                    label={t('Submit')}
                                    className='submit-btn'
                                />
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default CreateGLPostSetting;
